import { cn } from "@/lib/utils";
import { InputHTMLAttributes, ReactNode, useState, forwardRef, Ref } from "react";
import { MaskOptions, useMask } from "@react-input/mask";

interface BaseInputStyleProps {
  className?: string;
  error?: boolean;
  errorMessage?: string;
  helperText?: string;
}

interface BaseInputProps extends InputHTMLAttributes<HTMLInputElement>, BaseInputStyleProps {
  id: string;
  icon: ReactNode;
  placeholder: string;
  focusDisabled?: boolean;
  prefix?: string;
}

const BaseInputComponent = forwardRef<HTMLInputElement, BaseInputProps>((props, ref) => {
  const { focusDisabled = false, prefix, ...restProps } = props;

    const [shadow, setShadow] = useState(false);
    const [touched, setTouched] = useState(false);

    const showError = () => touched && props.error

    return (
      <div className="w-full">
        <div
          onClick={() => {
            if (focusDisabled) return;
            document.getElementById(props.id)?.focus();
          }}
          className={cn(
            `shadow w-full py-2 px-4 rounded-[24px] flex items-center gap-2 border bg-white duration-200`,
            showError() ? "!border-secondary" : "border-slate-300",
            shadow && "shadow-lg",
            props.className,
          )}
        >
          <div className="p-2 text-assistant">{props.icon}</div>

          {prefix && (
            <span className="text-sm font-bold text-gray-400 select-none">{prefix}</span>
          )}

          <input
            {...restProps}
            ref={ref}
            onFocus={() => setShadow(true)}
            onBlur={() => {
              setShadow(false);
              setTouched(true);
            }}
            className={cn(
              `py-2 w-full text-sm font-bold outline-none text-primary placeholder:text-assistant`,
            )}
          />
        </div>

        {props.helperText && (
          <div className="text-slate-400 text-[12px] mt-1.5 pl-6">
            <span>{props.helperText}</span>
          </div>
        )}

        {showError() && props.errorMessage && (
          <div className="text-secondary text-[12px] mt-1.5 pl-6">
            <span>{props.errorMessage}</span>
          </div>
        )}
      </div>
    );
  },
);

BaseInputComponent.displayName = "BaseInputComponent";

interface InputMaskProps extends BaseInputProps {
  maskOptions: MaskOptions;
}

export const Input = forwardRef<HTMLInputElement, BaseInputProps>(
  (props, ref) => {
    return <BaseInputComponent {...props} ref={ref} />;
  },
);

Input.displayName = "Input";

export const InputMask = forwardRef<HTMLInputElement, InputMaskProps>(
  (props, ref) => {
    const { maskOptions } = props;
    const inputRef = useMask(maskOptions);

    return <BaseInputComponent {...props} ref={inputRef} />;
  },
);

InputMask.displayName = "InputMask";

